import React, { useEffect, useState } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  Image,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { MessageModel } from "common247";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useAuth } from "@components/auth/useAuth";
import { useFirebase } from "@components/firebase/useFirebase";
import { actionTask } from "@services/firebase/workflow-functions";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedText } from "@/components/ThemedText";
import { ThemedButton } from "@/components/ThemedButton";
import { BrandColors } from "@/constants/themes/base";
import { FeedbackIcon } from "@/components/feedback/FeedbackIcon";
import { ThemedEntityLink } from "@/components/ThemedEntityLink";
import { Ionicons } from "@expo/vector-icons";

/**
 * Properties for the MessageDetailView component.
 */
export interface IMessageDetailViewProps {
  /** The message object to inspect */
  message: MessageModel;
  /** Callback triggered to mark message as read */
  onMarkRead?: (messageId: string) => void;
  /** Optional callback triggered to mark message as unread */
  onMarkUnread?: (messageId: string) => void;
  /** Optional callback to reply to the sender or group */
  onReply?: (message: MessageModel) => void;
  /** Optional callback to move message to trash */
  onMoveToTrash?: (messageId: string) => void;
  /** Optional callback to restore message from trash */
  onRestore?: (messageId: string) => void;
  /** Optional callback when dismissed / closed */
  onClose?: () => void;
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
  /** Whether to show the close button in the header (default: false) */
  showCloseButton?: boolean;
}

export const MESSAGE_TYPE_THEME: Record<string, { color: string; bg: string; border: string }> = {
  General: { color: "#4A608F", bg: "rgba(161, 174, 209, 0.16)", border: "#BAC4DF" },
  System: { color: "#161F36", bg: "rgba(22, 31, 54, 0.08)", border: "#CBD5E1" },
  Alert: { color: BrandColors.beetRed.hex, bg: "rgba(171, 23, 23, 0.08)", border: "rgba(171, 23, 23, 0.28)" },
  Warning: { color: "#B45309", bg: "rgba(255, 242, 77, 0.22)", border: "rgba(180, 83, 9, 0.28)" },
  Feedback: { color: BrandColors.sungoldOrange.hex, bg: "rgba(255, 91, 3, 0.08)", border: "rgba(255, 91, 3, 0.28)" },
  Task: { color: "#059669", bg: "rgba(5, 150, 105, 0.12)", border: "rgba(5, 150, 105, 0.3)" },
};

/**
 * Reusable view component displaying full contents and metadata of an internal message.
 * Adheres strictly to the Archival Heirloom aesthetic with sharp corners (borderRadius: 0).
 */
export function MessageDetailView({
  message,
  onMarkRead,
  onMarkUnread,
  onReply,
  onMoveToTrash,
  onRestore,
  onClose,
  style,
  showCloseButton = false,
}: IMessageDetailViewProps): React.JSX.Element {
  const cardBg = useThemeColor({}, "card");
  const textColor = useThemeColor({}, "text");
  const borderColor = useThemeColor({}, "border");
  const subtextColor = useThemeColor({}, "textMuted");
  const readOnlyBg = useThemeColor({}, "inputReadOnly");
  const { user } = useAuth();
  const services = useFirebase();
  const [taskActionLoading, setTaskActionLoading] = useState<"accept" | "reject" | null>(null);
  const [taskActionSuccess, setTaskActionSuccess] = useState<string | null>(null);

  const isGroup = Boolean(
    message && message.recipients?.some((r) => Boolean(r.groupId))
  );

  const getRecipientLabel = (): string => {
    if (!message) return "";
    if (message.recipientDisplayName?.trim()) {
      return isGroup
        ? `👥 ${message.recipientDisplayName.trim()}`
        : `👤 ${message.recipientDisplayName.trim()}`;
    }
    if (message.targetName?.trim()) {
      return isGroup ? `👥 ${message.targetName.trim()}` : `👤 ${message.targetName.trim()}`;
    }
    if (message.recipients && message.recipients.length > 0) {
      if (isGroup) {
        const groupName = message.recipients.find((r) => Boolean(r.groupName))?.groupName;
        return groupName ? `👥 ${groupName}` : `👥 Group (${message.recipients.length} members)`;
      }
      const names = message.recipients.map((r) => r.displayName?.trim()).filter(Boolean);
      if (names.length > 0) {
        return `👤 ${names.join(", ")}`;
      }
      if (message.recipients.length === 1) {
        return "👤 Recipient";
      }
      return `👤 ${message.recipients.length} recipients`;
    }
    return "👤 Recipient";
  };
  const recipientDisplay = getRecipientLabel();

  const currentUserId = user ? (user.userId || user.uid) : "";

  const isDirectSender = Boolean(
    user && message && (
      (typeof message.isSent === "boolean" && message.isSent) ||
      (message.senderUserId && (message.senderUserId === currentUserId || message.senderUserId === (user.userId || user.uid))) ||
      (message.senderContactId && (message.senderContactId === currentUserId || message.senderContactId === (user.userId || user.uid))) ||
      (user.email && message.senderEmail && message.senderEmail.toLowerCase() === user.email.toLowerCase())
    )
  );

  const isRecipient = Boolean(
    user &&
    message &&
    ((typeof message.isRecipient === "boolean" && message.isRecipient) ||
      message.recipients?.some((r) => r.contactId === currentUserId || (user.uid && r.contactId === user.uid)))
  );

  const canMarkReadState = !isDirectSender || isRecipient;

  const isTrashed = Boolean(
    message &&
    (message.isTrashed ||
     (user && message.recipients?.find((r) => r.contactId === currentUserId || (user.uid && r.contactId === user.uid))?.trashed))
  );

  useEffect(() => {
    if (message && !message.isRead && canMarkReadState && onMarkRead) {
      onMarkRead(message.id);
    }
    setTaskActionSuccess(null);
    setTaskActionLoading(null);
  }, [message?.id, onMarkRead, canMarkReadState]);

  const handleTaskAction = async (action: "accept" | "reject") => {
    if (!services || !message?.actionData?.taskId || taskActionLoading !== null) return;
    setTaskActionLoading(action);
    try {
      const response = await actionTask(services, {
        taskId: message.actionData.taskId,
        action,
      });
      if (response.status === true) {
        setTaskActionSuccess(`Task successfully ${action === "accept" ? "accepted" : "rejected"}!`);
      } else {
        CustomAlert.alert("Error", response.error || "Failed to execute task action");
      }
    } catch (err: any) {
      CustomAlert.alert("Error", err.message || "Failed to execute task action");
    } finally {
      setTaskActionLoading(null);
    }
  };

  const typeConfig = MESSAGE_TYPE_THEME[message.type] || MESSAGE_TYPE_THEME.General;
  const dateStr = message.createdAt
    ? new Date(message.createdAt).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";

  const imageMatch = message.body?.match(/!\[.*?\]\((data:image\/[a-zA-Z]+;base64,[^\)]+|https?:\/\/[^\)]+)\)/);
  const screenshotUri = imageMatch ? imageMatch[1] : null;
  const cleanBody = screenshotUri
    ? message.body.replace(imageMatch![0], "").trim()
    : message.body;

  const handleReplyPress = () => {
    if (onReply) {
      onReply(message);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: cardBg, borderColor }, style]}>
      {/* Header */}
      <View style={[styles.headerRow, { borderBottomColor: borderColor }]}>
        <View style={styles.headerLeft}>
          <View
            style={[
              styles.typeBadge,
              {
                backgroundColor: typeConfig.bg,
                borderColor: typeConfig.border,
              },
            ]}
          >
            {message.type === "Feedback" ? (
              <FeedbackIcon
                size={12}
                color={typeConfig.color}
                style={{ marginRight: 4 }}
              />
            ) : null}
            <ThemedText style={[styles.typeBadgeText, { color: typeConfig.color }]}>
              {message.type}
            </ThemedText>
          </View>
          <ThemedText style={[styles.headerDate, { color: subtextColor }]}>
            {dateStr}
          </ThemedText>
        </View>

        {showCloseButton && onClose ? (
          <TouchableOpacity
            onPress={onClose}
            style={styles.closeButton}
            accessibilityLabel="Close message detail"
          >
            <ThemedText style={[styles.closeIcon, { color: subtextColor }]}>✕</ThemedText>
          </TouchableOpacity>
        ) : null}
      </View>

      {/* Body Content */}
      <ScrollView
        style={styles.bodyScroll}
        contentContainerStyle={styles.bodyContent}
      >
        {/* Subject */}
        <ThemedText style={[styles.subjectTitle, { color: textColor }]}>
          {message.subject}
        </ThemedText>

        {/* Sender and Target Metadata Tray */}
        <View
          style={[
            styles.metaBox,
            {
              backgroundColor: readOnlyBg,
              borderColor: borderColor,
            },
          ]}
        >
          <View style={styles.metaRow}>
            <ThemedText style={[styles.metaLabel, { color: subtextColor }]}>
              From:
            </ThemedText>
            {message.senderContactId || message.senderUserId ? (
              <ThemedEntityLink
                type="contact"
                id={message.senderContactId || message.senderUserId || ""}
                displayName={`${message.senderDisplayName || "Sender"}${message.senderEmail ? ` (${message.senderEmail})` : ""}`}
                variant="inline"
              />
            ) : (
              <ThemedText style={[styles.metaValue, { color: textColor }]}>
                {message.senderDisplayName}{" "}
                {message.senderEmail ? `(${message.senderEmail})` : ""}
              </ThemedText>
            )}
          </View>

          <View style={styles.metaRow}>
            <ThemedText style={[styles.metaLabel, { color: subtextColor }]}>
              To:
            </ThemedText>
            {isGroup ? (
              (() => {
                const targetGroupId = message.recipients?.find((r) => Boolean(r.groupId))?.groupId;
                return targetGroupId ? (
                  <ThemedEntityLink
                    type="group"
                    id={targetGroupId}
                    displayName={recipientDisplay}
                    variant="inline"
                  />
                ) : (
                  <ThemedText style={[styles.metaValue, { color: textColor }]}>
                    {recipientDisplay}
                  </ThemedText>
                );
              })()
            ) : (
              (() => {
                const targetContactId = message.recipients?.[0]?.contactId || message.targetUserId;
                return targetContactId ? (
                  <ThemedEntityLink
                    type="contact"
                    id={targetContactId}
                    displayName={recipientDisplay}
                    variant="inline"
                  />
                ) : (
                  <ThemedText style={[styles.metaValue, { color: textColor }]}>
                    {recipientDisplay}
                  </ThemedText>
                );
              })()
            )}
          </View>

          {(message.actionData as any)?.memoryId ? (
            <View style={styles.metaRow}>
              <ThemedText style={[styles.metaLabel, { color: subtextColor }]}>
                Attached:
              </ThemedText>
              <ThemedEntityLink
                type="memory"
                id={(message.actionData as any).memoryId}
                displayName="View Referenced Memory"
                showIcon={true}
                variant="badge"
              />
            </View>
          ) : null}

          {message.recipients && message.recipients.length > 0 ? (
            <View style={styles.metaRow}>
              <ThemedText style={[styles.metaLabel, { color: subtextColor }]}>
                Status:
              </ThemedText>
              <ThemedText style={[styles.metaValue, { color: textColor }]}>
                {message.recipients.filter((r) => r.read).length}/{message.recipients.length} Read
                {message.recipients.some((r) => r.trashed)
                  ? ` • ${message.recipients.filter((r) => r.trashed).length} Trashed`
                  : ""}
              </ThemedText>
            </View>
          ) : null}
        </View>

        {/* Full Message Text */}
        <ThemedText style={[styles.messageBody, { color: textColor }]}>
          {cleanBody}
        </ThemedText>

        {/* Task Action Box if message action is Task */}
        {(message.action === "task" || message.action === "Task") && message.actionData?.taskId ? (
          <View style={[styles.taskActionBox, { borderColor: "#059669", backgroundColor: "rgba(5, 150, 105, 0.08)" }]}>
            <View style={styles.taskActionHeader}>
              <Ionicons name="clipboard-outline" size={18} color="#059669" />
              <ThemedText style={[styles.taskActionTitle, { color: "#059669" }]}>
                Workflow Task Action
              </ThemedText>
            </View>
            <ThemedText style={[styles.taskActionDescription, { color: textColor }]}>
              {taskActionSuccess || "You have been assigned to action this workflow task. Review the details above and select an action below:"}
            </ThemedText>
            {!taskActionSuccess ? (
              <View style={styles.taskActionButtonsRow}>
                <ThemedButton
                  title={taskActionLoading === "reject" ? "Rejecting..." : "Reject Task"}
                  variant="danger"
                  onPress={() => handleTaskAction("reject")}
                  disabled={taskActionLoading !== null}
                />
                <ThemedButton
                  title={taskActionLoading === "accept" ? "Accepting..." : "Accept Task"}
                  variant="primary"
                  onPress={() => handleTaskAction("accept")}
                  disabled={taskActionLoading !== null}
                />
              </View>
            ) : null}
          </View>
        ) : null}

        {/* Attached Screenshot if present */}
        {screenshotUri ? (
          <View style={[styles.screenshotBox, { borderColor }]}>
            <ThemedText style={[styles.screenshotLabel, { color: subtextColor }]}>
              Attached Screen Context:
            </ThemedText>
            <Image
              source={{ uri: screenshotUri }}
              style={styles.screenshotImage}
              resizeMode="contain"
            />
          </View>
        ) : null}
      </ScrollView>

      {/* Footer Actions */}
      <View style={[styles.footerRow, { borderTopColor: borderColor }]}>
        {showCloseButton && onClose ? (
          <ThemedButton
            title="Close"
            variant="minimal"
            onPress={onClose}
          />
        ) : null}

        {isTrashed && onRestore ? (
          <ThemedButton
            title="Restore"
            variant="secondary"
            onPress={() => onRestore(message.id)}
          />
        ) : null}

        {!isTrashed && onMarkUnread && canMarkReadState ? (
          <ThemedButton
            title="Mark as Unread"
            variant="secondary"
            onPress={() => {
              onMarkUnread(message.id);
              onClose?.();
            }}
          />
        ) : null}

        {!isTrashed && onMoveToTrash ? (
          <ThemedButton
            title="Move to Trash"
            variant="danger"
            onPress={() => onMoveToTrash(message.id)}
          />
        ) : null}

        {onReply && !isTrashed ? (
          <ThemedButton
            title="Reply"
            variant="primary"
            onPress={handleReplyPress}
          />
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    borderRadius: 0,
    borderWidth: 1,
    overflow: "hidden",
    flex: 1,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  typeBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 0,
    borderWidth: 1,
  },
  typeBadgeText: {
    fontSize: 11,
    fontWeight: "700",
  },
  headerDate: {
    fontSize: 12,
  },
  closeButton: {
    padding: 4,
  },
  closeIcon: {
    fontSize: 16,
    fontWeight: "700",
  },
  bodyScroll: {
    flex: 1,
  },
  bodyContent: {
    padding: 16,
  },
  subjectTitle: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 12,
    lineHeight: 24,
  },
  metaBox: {
    borderWidth: 1,
    padding: 10,
    borderRadius: 0,
    marginBottom: 14,
    gap: 6,
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  metaLabel: {
    width: 65,
    fontSize: 12,
    fontWeight: "600",
  },
  metaValue: {
    flex: 1,
    fontSize: 12,
  },
  messageBody: {
    fontSize: 14,
    lineHeight: 22,
    marginBottom: 16,
  },
  taskActionBox: {
    borderWidth: 1,
    borderRadius: 0,
    padding: 12,
    marginTop: 10,
    marginBottom: 16,
    gap: 8,
  },
  taskActionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  taskActionTitle: {
    fontSize: 13,
    fontWeight: "700",
  },
  taskActionDescription: {
    fontSize: 13,
    lineHeight: 18,
  },
  taskActionButtonsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 6,
  },
  screenshotBox: {
    borderWidth: 1,
    borderRadius: 0,
    padding: 10,
    marginTop: 8,
    marginBottom: 16,
    backgroundColor: "rgba(0,0,0,0.02)",
  },
  screenshotLabel: {
    fontSize: 11,
    fontWeight: "600",
    marginBottom: 8,
  },
  screenshotImage: {
    width: "100%",
    height: 240,
    borderRadius: 0,
    backgroundColor: "#000",
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    gap: 10,
  },
});
